import type { SortDirection } from '@app/types';
import type { ListingDoc } from '@bb/domain/dist/schemas';
import type {
  AppointmentStatus,
  AppointmentType,
  BuildDTO,
  JsonObject,
  Timezone,
} from '@bb/domain/dist/types';
import type { CoopAppointmentsDTOV2 as _CoopAppointmentsDTOV2 } from '@bb/domain/dist/types/dto/AppointmentController/getAppointmentsForCoop';
import type { Org } from '@bb/automaton';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import moment from 'moment';

export type GetAppointmentsForCoopOptions = {
  useV2Logic: true;
  sortOrder: SortDirection;
  statuses: AppointmentStatus[];
  searchText?: string;
  page?: number;
  docType?: 'appointment' | 'virtualTour' | 'tourItem' | string[];
  startDate: Date | moment.Moment;
  endDate?: Date | moment.Moment;
  includeSignatures?: boolean;
  includeActionCount?: boolean;
  includeConfirmedPendingAppointment?: boolean;
  excludeConfirmedPendingAppointment?: boolean;
  includeSupplementaryTourItems?: boolean;
  statusFiltered?: boolean;
};

type OriginalAppointmentType = NonNullable<
  _CoopAppointmentsDTOV2['appointments']
>[number];

// TODO: move these changes to AppointmentESJsonType in domain repo and delete this
export type AppointmentESJsonType = BuildDTO<
  OriginalAppointmentType,
  {
    type: AppointmentType | 'APPOINTMENT' | 'ROUTE_STOP';
    docType: 'appointment' | 'virtualTour' | 'tourItem';
    listing: JsonObject<
      Pick<
        ListingDoc,
        | 'id'
        | 'line1'
        | 'line2'
        | 'thumbnail'
        | 'mls_number'
        | 'reps'
        | 'timezone'
        | 'location'
        | 'locations'
      > & { org: Org }
    >;
    nextItem?: string | null;
    isFirstItem?: boolean;
    appointmentTourId?: string;
    userName?: string;
    dateStr?: string;
    dateStrWithYear?: string;
    duration?: {
      hours: number;
      minutes: number;
      raw: number;
    };
    bookedThroughMls?: boolean;
    uid?: string;
    cancelledBy?: string;
    retsId?: string;
    tourName?: string;
    appointmentTourItemId?: string;
    locationName?: string;
    address?: string;
    status?: AppointmentStatus;
    timezone?: Timezone;
    buyers: {
      id: string;
      firstName: string;
      lastName: string;
    }[];
  }
>;

// TODO: move these changes to CoopAppointmentsDTO in domain repo and delete this
type CoopAppointmentsDTOV2 = Omit<_CoopAppointmentsDTOV2, 'appointments'> & {
  appointments: AppointmentESJsonType[];
  supplementaryItems?: AppointmentESJsonType[];
};

const getAppointmentsForCoop = async ({
  useV2Logic,
  sortOrder,
  statuses,
  searchText,
  page = 0,
  docType,
  startDate,
  endDate,
  includeSignatures,
  includeActionCount,
  includeConfirmedPendingAppointment,
  excludeConfirmedPendingAppointment,
  includeSupplementaryTourItems,
  statusFiltered,
}: GetAppointmentsForCoopOptions) => {
  const config: AxiosRequestConfig = {
    url: '/user/coop/appointments',
    method: 'get',
    params: {
      address: searchText,
      page,
      docType,
      startDate: moment(startDate).startOf('day').toISOString(),
      endDate: endDate ? moment(endDate).endOf('day').toISOString() : undefined,
      statuses,
      sortOrder,
      useV2Logic,
      includeSignatures,
      includeActionCount,
      includeConfirmedPendingAppointment,
      excludeConfirmedPendingAppointment,
      includeSupplementaryTourItems,
      statusFiltered,
    },
  };

  const { data } = await axios.request<CoopAppointmentsDTOV2>(config);

  return data;
};

export default getAppointmentsForCoop;
